import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import managedAxios from "@/utils/managedAxios";
import { API_BASE } from "@/App";
import {
  BookOpen, Target, TrendingUp, PenTool, Scale,
  ArrowLeft, ChevronRight, Clock, Flame, Sparkles,
  CheckCircle, Award, Trophy, Zap, BarChart3,
  Beaker, Globe, Heart, Anchor, FileText, Languages
} from "lucide-react";

/**
 * APPSC Mains Hub - Exact replica of UPSC MainsHubPage
 * Central hub for all APPSC Group-I Mains answer writing preparation
 */

// Exported for use by APPSCAnswerWritingPage (legacy route)
export const APPSC_PAPERS = [
  { id: "paper1", name: "Paper I — General Essay", short: "Essay", marks: 150 },
  { id: "paper2", name: "Paper II — History, Culture & Geography", short: "History & Culture", marks: 150 },
  { id: "paper3", name: "Paper III — Polity, Governance, Law & Ethics", short: "Polity & Ethics", marks: 150 },
  { id: "paper4", name: "Paper IV — Economy & Development", short: "Economy", marks: 150 },
  { id: "paper5", name: "Paper V — Science & Technology", short: "Sci & Tech", marks: 150 },
  { id: "english", name: "English Language Paper", short: "English", marks: 150 },
  { id: "telugu", name: "Telugu Language Paper", short: "Telugu", marks: 150 },
];

const APPSCMainsHubPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const isPro = user?.role === "PRO";

  // APPSC Group-I Mains exam countdown
  const MAINS_DATE = new Date('2026-10-15');
  const getDaysRemaining = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const exam = new Date(MAINS_DATE);
    exam.setHours(0, 0, 0, 0);
    return Math.ceil((exam - today) / (1000 * 60 * 60 * 24));
  };
  const daysToExam = getDaysRemaining();
  const weeksToExam = Math.floor(daysToExam / 7);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.get(`${API_BASE}/appsc/mains/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error("Error fetching APPSC stats:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureClick = (feature) => {
    navigate(feature.path);
  };

  // Paper Suites - Hero features (mirrors UPSC GS Suites)
  const paperSuites = [
    {
      title: "Paper I — Essay",
      subtitle: "General Essay",
      description: "Current Affairs, Socio-Political, Economic & Cultural Themes",
      icon: PenTool,
      path: "/appsc/paper/paper1",
      color: "from-rose-500 to-pink-500",
      gradient: "from-rose-500/10 to-pink-500/10",
      borderColor: "border-rose-200",
    },
    {
      title: "Paper II — History",
      subtitle: "History, Culture & Geography",
      description: "India & AP History, Culture, Art, Architecture & Geography",
      icon: BookOpen,
      path: "/appsc/paper/paper2",
      color: "from-orange-500 to-amber-500",
      gradient: "from-orange-500/10 to-amber-500/10",
      borderColor: "border-orange-200",
    },
    {
      title: "Paper III — Polity",
      subtitle: "Polity, Governance, Law & Ethics",
      description: "Constitution, Public Admin, Ethics, Law (Civil, Criminal, Cyber, Tax)",
      icon: Scale,
      path: "/appsc/paper/paper3",
      color: "from-blue-500 to-indigo-500",
      gradient: "from-blue-500/10 to-indigo-500/10",
      borderColor: "border-blue-200",
    },
    {
      title: "Paper IV — Economy",
      subtitle: "Economy & Development",
      description: "Indian & AP Economy, Budgeting, Agriculture, Industry, Infrastructure",
      icon: TrendingUp,
      path: "/appsc/paper/paper4",
      color: "from-emerald-500 to-teal-500",
      gradient: "from-emerald-500/10 to-teal-500/10",
      borderColor: "border-emerald-200",
    },
  ];

  // Paper V as a standalone feature (like UPSC's Essay Suite)
  const specialFeatures = [
    {
      title: "Paper V — Sci & Tech",
      description: "S&T, Space, Energy, Environment, Biotech & IPR",
      icon: Beaker,
      path: "/appsc/paper/paper5",
      color: "from-cyan-500 to-blue-500",
      stats: "7 topics",
    },
    {
      title: "English Language",
      description: "Essay, Letter, Precis, Grammar & Translation",
      icon: FileText,
      path: "/appsc/paper/english",
      color: "from-violet-500 to-purple-500",
      stats: "Language",
    },
    {
      title: "Telugu Language",
      description: "Vyasam, Precis, Grammar & Translation",
      icon: Languages,
      path: "/appsc/paper/telugu",
      color: "from-amber-500 to-orange-500",
      stats: "Language",
    },
  ];

  // Tools
  const toolFeatures = [
    { title: "Progress Report", description: "Your APPSC mains analytics", icon: BarChart3, path: "/appsc", color: "from-cyan-500 to-teal-500" },
    { title: "APPSC Dashboard", description: "Back to main dashboard", icon: Anchor, path: "/appsc", color: "from-violet-500 to-purple-500" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-blue-300">Loading APPSC Mains Hub...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/30" data-testid="appsc-mains-hub">

      {/* VIBRANT HEADER */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
          {/* Back button & Title */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/appsc")}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
                data-testid="appsc-mains-back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Anchor className="w-7 h-7" />
                  APPSC Mains Hub
                </h1>
                <p className="text-blue-200 text-sm">Master answer writing — Group-I</p>
              </div>
            </div>

            {/* Days Counter */}
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2">
              <p className="text-2xl font-bold leading-none">{daysToExam}</p>
              <p className="text-[10px] text-blue-200">days to Mains</p>
            </div>
          </div>

          {/* Quick Stats Row */}
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-xl font-bold">{stats?.total_answers || 0}</p>
              <p className="text-[10px] text-blue-200">Answers</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-xl font-bold">{stats?.avg_score || 0}</p>
              <p className="text-[10px] text-blue-200">Avg Score</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-xl font-bold">{stats?.total_evaluations || 0}</p>
              <p className="text-[10px] text-blue-200">Evaluations</p>
            </div>
            <div className="bg-gradient-to-br from-amber-500/30 to-orange-500/30 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-xl font-bold">{weeksToExam}</p>
              <p className="text-[10px] text-amber-200">Weeks Left</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* TOPPER TIP BANNER */}
        <Card className="border-0 shadow-lg bg-gradient-to-r from-blue-100 to-indigo-100 overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-blue-900">Topper's Secret</p>
                <p className="text-sm text-blue-700">"Write 1 answer daily for each paper. Use AP-specific examples generously."</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* PAPER SUITES - HERO GRID (2x2) */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-5 h-5 text-blue-500" />
            <h2 className="font-bold font-heading text-lg text-slate-800">Paper Answer Writing Suites</h2>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {paperSuites.map((suite, idx) => (
              <Card
                key={idx}
                className={`border-2 ${suite.borderColor} bg-gradient-to-br ${suite.gradient} cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all group overflow-hidden`}
                onClick={() => handleFeatureClick(suite)}
                data-testid={`appsc-suite-${idx}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 bg-gradient-to-br ${suite.color} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg`}>
                      <suite.icon className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold font-heading text-lg text-slate-800">{suite.title}</h3>
                      <p className="text-xs text-slate-500 mb-1">{suite.subtitle}</p>
                      <p className="text-sm text-slate-600">{suite.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-200/50">
                    <Badge variant="outline" className="text-xs">
                      {isPro ? "Unlimited answers" : "1 free answer"}
                    </Badge>
                    <div className="flex items-center gap-1 text-sm font-medium text-slate-600">
                      Start Writing
                      <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* SPECIAL FEATURES (Paper V + Languages) */}
        <div className="grid grid-cols-3 gap-4">
          {specialFeatures.map((feature, idx) => (
            <Card
              key={idx}
              className={`border-0 shadow-xl cursor-pointer hover:scale-[1.02] transition-all overflow-hidden bg-gradient-to-br ${feature.color}`}
              onClick={() => handleFeatureClick(feature)}
              data-testid={`appsc-special-${idx}`}
            >
              <CardContent className="p-5 text-white">
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <feature.icon className="w-6 h-6" />
                    </div>
                  </div>
                  <h3 className="font-bold font-heading text-base mb-1">{feature.title}</h3>
                  <p className="text-white/80 text-sm mb-3 flex-1">{feature.description}</p>
                  <div className="flex items-center justify-between">
                    <Badge className="bg-white/20 text-white border-0 text-xs">
                      {feature.stats}
                    </Badge>
                    <ChevronRight className="w-5 h-5 text-white/60" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* TOOLS */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-5 h-5 text-indigo-500" />
            <h2 className="font-bold font-heading text-lg text-slate-800">Tools & Analytics</h2>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {toolFeatures.map((feature, idx) => (
              <Card
                key={idx}
                className="border border-slate-200 bg-white cursor-pointer hover:shadow-lg hover:border-transparent transition-all group overflow-hidden"
                onClick={() => handleFeatureClick(feature)}
              >
                <CardContent className="p-4 text-center">
                  <div className={`w-12 h-12 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform shadow-md`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="font-medium text-sm text-slate-800">{feature.title}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* WRITING STREAK / PROGRESS */}
        <Card className="border border-slate-200 bg-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Flame className="w-5 h-5 text-orange-500" />
                <span className="font-semibold text-slate-800">This Week's Progress</span>
              </div>
              <Badge variant="outline" className="text-xs">
                {stats?.answers_this_week || 0} answers
              </Badge>
            </div>
            <div className="grid grid-cols-7 gap-2">
              {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, idx) => {
                const isActive = stats?.days_active?.includes(idx) || false;
                return (
                  <div key={idx} className="text-center">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mx-auto mb-1 ${
                      isActive
                        ? 'bg-gradient-to-br from-blue-500 to-indigo-500 text-white'
                        : 'bg-slate-100 text-slate-400'
                    }`}>
                      {isActive ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        <span className="text-xs font-medium">{day}</span>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-500">{day}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* EXAM PATTERN INFO */}
        <Card className="border border-blue-200 bg-blue-50/50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Target className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-blue-800">APPSC Group-I Mains Pattern</p>
                <p className="text-sm text-blue-700">7 Papers | 150 marks each | 150 minutes each | Total: 1050 marks | Medium: English or Telugu</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* PRO UPGRADE CTA (for FREE users) */}
        {!isPro && (
          <Card
            className="border-2 border-blue-300 bg-gradient-to-r from-blue-50 to-indigo-50 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate("/upgrade")}
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Trophy className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-bold font-heading text-lg text-slate-800">Upgrade to CLARIAS PRO</p>
                  <p className="text-sm text-slate-600">Unlock unlimited answers, full evaluations & more</p>
                </div>
                <ChevronRight className="w-6 h-6 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        )}

        <div className="h-8" />
      </div>
    </div>
  );
};

export default APPSCMainsHubPage;
